import { Link } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import { PageHero } from '@/components/PageHero';
import { Reveal } from '@/components/Reveal';
import { BlogCover } from '@/components/illustrations/BlogCover';
import { posts } from '@/content/blog';
import { formatPostDate } from '@/content/format';
import { useSeo } from '@/seo/useSeo';

export function Blog(): JSX.Element {
  const sorted = [...posts].sort((a, b) => (a.date < b.date ? 1 : -1));
  const [featured, ...rest] = sorted;

  useSeo({
    title: 'Blog',
    description:
      'Notes on agentic AI, RMM/PSA architecture, security, and compliance — written honestly about what Nexus is building, not what it claims to have shipped.',
    path: '/blog',
    schema: {
      '@type': 'ItemList',
      itemListElement: sorted.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://nexusrmm.ai/blog/${p.slug}`,
        name: p.title,
      })),
    },
  });

  return (
    <div>
      <PageHero
        eyebrow="From the team"
        title="Notes on building an agentic PSA + RMM platform."
        subtitle="AI architecture, security design decisions, and honest looks at what compliance tooling should actually do — written as we build, not as marketing copy after the fact."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content">
          {featured && (
            <Reveal>
            <Link
              to={`/blog/${featured.slug}`}
              className="group card-lift mb-10 grid grid-cols-1 gap-6 rounded-2xl border border-white/10 bg-white/[0.02] p-4 no-underline md:grid-cols-2 md:gap-8 md:p-6"
            >
              <BlogCover cover={featured.cover} size="lg" />
              <div className="flex flex-col justify-center">
                <div className="mb-3 flex items-center gap-2">
                  <Chip
                    label={featured.tag}
                    size="small"
                    className="h-[22px] border border-nexus-purple/35 bg-transparent text-[10.5px] font-bold text-nexus-purple-light"
                  />
                  <span className="text-xs text-white/40">
                    {formatPostDate(featured.date)} · {featured.readMinutes} min read
                  </span>
                </div>
                <h2 className="font-display text-2xl font-bold leading-snug text-nexus-ink md:text-[28px]">
                  {featured.title}
                </h2>
                <p className="mt-3 text-sm text-white/65">{featured.excerpt}</p>
              </div>
            </Link>
            </Reveal>
          )}

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 90} className="flex">
              <Link
                to={`/blog/${p.slug}`}
                className="group card-lift flex flex-1 flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-4 no-underline"
              >
                <BlogCover cover={p.cover} />
                <div className="mt-4 flex items-center gap-2">
                  <Chip
                    label={p.tag}
                    size="small"
                    className="h-[20px] border border-nexus-purple/35 bg-transparent text-[10px] font-bold text-nexus-purple-light"
                  />
                  <span className="text-[11px] text-white/40">
                    {formatPostDate(p.date)} · {p.readMinutes} min
                  </span>
                </div>
                <h3 className="mt-2.5 font-display text-[16px] font-semibold leading-snug text-nexus-ink">
                  {p.title}
                </h3>
                <p className="mt-1.5 flex-1 text-sm text-white/65">{p.excerpt}</p>
              </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
